import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import PrintIcon from '@mui/icons-material/Print';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link, useSearchParams } from 'react-router-dom';

import { useVma } from '../Allures/useVma.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { type ZoneRow } from '../Construction/allureTable.ts';
import BraceletSvg, { POLICE_BRACELET } from './BraceletSvg.tsx';
import {
  BANDE,
  PAPIERS,
  allureMontre,
  calculerPassages,
  facteurGps,
  decouperEnBandes,
  formaterChrono,
  formaterKm,
  libellePassage,
  modeleAllure,
  type Papier,
  type ParametresBracelet,
  type Reperes,
  type Strategie,
} from './braceletEngine.ts';
import { estCoach, useMoi } from '../Auth/moi.ts';

// Memes distances que la page Temps de passage (code = zone d'allure dediee).
const DISTANCES = [
  { code: '3K', libelle: '3 km', distanceKm: 3, nom: '3 km' },
  { code: '5K', libelle: '5 km', distanceKm: 5, nom: '5 km' },
  { code: '10K', libelle: '10 km', distanceKm: 10, nom: '10 km' },
  { code: 'HP', libelle: 'Semi', distanceKm: 21.0975, nom: 'Semi-marathon' },
  { code: 'MP', libelle: 'Marathon', distanceKm: 42.195, nom: 'Marathon' },
  { code: 'AUTRE', libelle: 'Autre', distanceKm: NaN, nom: 'Course' },
];

type Mode = 'zone' | 'temps' | 'allure';

// Intervalle des passages par defaut : tous les 5 km, sauf sur les courses
// de 5 km et moins ou il ne resterait que l'arrivee (tous les km).
function pasParDefaut(distanceKm: number): number {
  return isFinite(distanceKm) && distanceKm <= 10 ? 1 : 5;
}

const CLE_CORRECTION_GPS = 'bracelet.correctionGps';

function lireCorrectionGps(): number {
  try {
    const v = parseFloat(localStorage.getItem(CLE_CORRECTION_GPS) ?? '');
    return isFinite(v) && v >= 0 && v <= 3 ? v : 0;
  } catch {
    return 0;
  }
}

const nombre = (s: string) => {
  const v = parseFloat(s.replace(',', '.'));
  return isFinite(v) ? v : NaN;
};

// VMA ajustee de la page Temps de passage (404 = aucune).
function chargerVmaPassage(): Promise<number | null> {
  return fetch(new Request('/api/preference/vma-passage', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
    },
  })).then((r) => (r.status === 200 ? (r.json() as Promise<number>) : null));
}

/** Feuille A4 paysage a imprimer : bandes a taille reelle + regle de controle. */
function feuilleImpression(parametres: ParametresBracelet, bandes: ReturnType<typeof decouperEnBandes>): string {
  const svgs = renderToStaticMarkup(
    <>
      {bandes.map((b, i) => (
        <div className="bande" key={i}>
          <BraceletSvg parametres={parametres} passages={b} index={i} total={bandes.length} impression />
        </div>
      ))}
    </>,
  );
  const graduations = Array.from({ length: 6 }, (_, i) =>
    `<line x1="${i * 10}" y1="0" x2="${i * 10}" y2="${i % 5 === 0 ? 4 : 2.5}" stroke="#000" stroke-width="0.2"/>`).join('');
  const objectif = formaterChrono(parametres.tempsSec, parametres.tempsSec >= 3600);
  const nom = (parametres.nom || 'Course').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Bracelet ${nom} ${objectif}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700;800&display=swap">
<style>@page{size:A4 landscape;margin:12mm}body{margin:0;font-family:${POLICE_BRACELET};color:#111;-webkit-print-color-adjust:exact;print-color-adjust:exact}
h1{font-size:20pt;margin:0 0 2mm;text-transform:uppercase}p{margin:0 0 4mm;font-size:11pt}.bande{margin:0 0 7mm;break-inside:avoid}
.regle{display:flex;gap:4mm;align-items:center;font-size:10pt;margin-top:2mm}</style></head><body>
<h1>${nom} · objectif ${objectif}</h1>
<p>Imprimer à 100 % (taille réelle, sans « ajuster à la page »). Découper sur les pointillés, recouvrir de ruban adhésif, refermer sur la zone « COLLER ».</p>
${svgs}
<div class="regle"><svg width="50mm" height="4mm" viewBox="0 0 50 4"><line x1="0" y1="0" x2="50" y2="0" stroke="#000" stroke-width="0.3"/>${graduations}<line x1="50" y1="0" x2="50" y2="4" stroke="#000" stroke-width="0.2"/></svg><span>Contrôle : ce trait doit mesurer 50 mm.</span></div>
<script>document.fonts.ready.then(function(){setTimeout(function(){window.print()},200)})</script>
</body></html>`;
}

/**
 * Bracelet d'allure : temps de passage cumules pour une course, a imprimer
 * et porter au poignet. L'objectif vient d'une zone d'allure de
 * l'utilisateur (VMA x %VMA), d'un temps final ou d'une allure.
 */
export default function Bracelet() {
  // Zones d'allure : reglees par le coach, lien vers les preferences pour lui seul.
  const moi = useMoi();
  const [params] = useSearchParams();
  const [vmaReference] = useVma();
  const { table } = useAllureTable();

  const codeInitial = DISTANCES.some((d) => d.code === params.get('distance')) ? (params.get('distance') as string) : 'MP';
  const distanceInitiale = DISTANCES.find((d) => d.code === codeInitial)!;

  const [codeDistance, setCodeDistance] = React.useState(codeInitial);
  const [distanceAutre, setDistanceAutre] = React.useState('15');
  const [nom, setNom] = React.useState(distanceInitiale.nom);

  const [mode, setMode] = React.useState<Mode>('zone');
  const [codeZone, setCodeZone] = React.useState(codeInitial === 'AUTRE' ? 'MP' : codeInitial);
  const [vmaSaisie, setVmaSaisie] = React.useState<string | null>(params.get('vma'));
  const [heures, setHeures] = React.useState('3');
  const [minutes, setMinutes] = React.useState('30');
  const [secondes, setSecondes] = React.useState('0');
  const [allureMin, setAllureMin] = React.useState('4');
  const [allureSec, setAllureSec] = React.useState('58');

  const [strategie, setStrategie] = React.useState<Strategie>('negative');
  const [ecartPct, setEcartPct] = React.useState(1.5);
  const [pasKm, setPasKm] = React.useState(pasParDefaut(distanceInitiale.distanceKm));
  const [afficherAllure, setAfficherAllure] = React.useState(true);
  const [miCourse, setMiCourse] = React.useState(true);
  // Le passage a mi-course (semi) ne concerne que le marathon.
  const estMarathon = codeDistance === 'MP';
  const [papier, setPapier] = React.useState<Papier>('blanc');
  // Correction GPS (%) : la montre mesure en general un peu plus que la
  // distance officielle. Memorisee dans le navigateur (propre a la montre).
  const [correctionPct, setCorrectionPctEtat] = React.useState<number>(lireCorrectionGps);
  const setCorrectionPct = (v: number) => {
    setCorrectionPctEtat(v);
    try { localStorage.setItem(CLE_CORRECTION_GPS, String(v)); } catch { /* non memorisee */ }
  };
  const [reperes, setReperes] = React.useState<Reperes>('panneaux');
  const [message, setMessage] = React.useState('');

  // VMA : celle passee par la page Temps de passage (?vma=), sinon la VMA
  // ajustee pour les passages, sinon la VMA de reference. Une saisie dans le
  // champ prend le dessus, pour cette page seulement.
  const [vmaPassage, setVmaPassage] = React.useState<number | null>(null);
  React.useEffect(() => {
    chargerVmaPassage().then(setVmaPassage).catch(() => undefined);
  }, []);
  const vmaTexte = vmaSaisie ?? String(vmaPassage ?? vmaReference);

  const distance = DISTANCES.find((d) => d.code === codeDistance)!;
  const distanceKm = codeDistance === 'AUTRE' ? nombre(distanceAutre) : distance.distanceKm;
  const vma = nombre(vmaTexte);

  const zones = React.useMemo<ZoneRow[]>(
    () => table.filter((z: ZoneRow) => z.code !== 'RECUP' && isFinite(z.pctVMA) && z.pctVMA > 0),
    [table],
  );
  const zone = zones.find((z) => z.code === codeZone);

  let tempsSec = NaN;
  if (mode === 'zone') tempsSec = zone && vma > 0 ? (distanceKm * 3600) / (vma * zone.pctVMA) : NaN;
  else if (mode === 'temps') tempsSec = (nombre(heures) || 0) * 3600 + (nombre(minutes) || 0) * 60 + (nombre(secondes) || 0);
  else tempsSec = ((nombre(allureMin) || 0) * 60 + (nombre(allureSec) || 0)) * distanceKm;

  const valide = distanceKm > 0 && tempsSec > 0 && isFinite(tempsSec);

  const parametres: ParametresBracelet = {
    nom, distanceKm, tempsSec, strategie, ecart: ecartPct / 100, pasKm, afficherAllure, miCourse: estMarathon && miCourse, papier,
    correctionGps: correctionPct / 100, reperes,
  };
  const avecGps = facteurGps(parametres) > 1;
  const passages = React.useMemo(
    () => (valide ? calculerPassages(parametres) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valide, nom, distanceKm, tempsSec, strategie, ecartPct, pasKm, afficherAllure, estMarathon, miCourse, papier, correctionPct, reperes],
  );
  const bandes = React.useMemo(() => decouperEnBandes(passages), [passages]);
  const modele = valide ? modeleAllure(parametres) : null;
  const avecHeures = tempsSec >= 3600;

  const changerDistance = (code: string | null) => {
    const d = DISTANCES.find((x) => x.code === code);
    if (!d) return;
    if (DISTANCES.some((x) => x.nom === nom) || !nom) setNom(d.nom);
    if (d.code !== 'AUTRE' && zones.some((z) => z.code === d.code)) setCodeZone(d.code);
    setCodeDistance(d.code);
    setPasKm(pasParDefaut(d.code === 'AUTRE' ? nombre(distanceAutre) : d.distanceKm));
  };

  // En passant en saisie libre, on part de l'objectif courant.
  const changerMode = (m: Mode | null) => {
    if (!m) return;
    if (valide) {
      const t = Math.round(tempsSec);
      setHeures(String(Math.floor(t / 3600)));
      setMinutes(String(Math.floor((t % 3600) / 60)));
      setSecondes(String(t % 60));
      const a = Math.round(tempsSec / distanceKm);
      setAllureMin(String(Math.floor(a / 60)));
      setAllureSec(String(a % 60));
    }
    setMode(m);
  };

  const imprimer = () => {
    const fenetre = window.open('', '_blank');
    if (!fenetre) {
      setMessage('Fenêtre bloquée par le navigateur : autorise les fenêtres pour ce site.');
      return;
    }
    fenetre.document.open();
    fenetre.document.write(feuilleImpression(parametres, bandes));
    fenetre.document.close();
  };

  const copier = () => {
    const texte = passages
      .map((p) => `${libellePassage(p)}\t${formaterChrono(p.temps, avecHeures)}\t${formaterChrono(p.allureTroncon)}/km`)
      .join('\n');
    navigator.clipboard.writeText(texte).then(
      () => setMessage('Temps copiés.'),
      () => setMessage('Copie impossible : sélectionne le tableau.'),
    );
  };

  const champ = { size: 'small' as const, type: 'number' };

  return (
    <Box component="main" sx={{ p: 12, pt: 10 }}>
      <Typography sx={{ mb: 3 }} color="text.secondary">
        Tes temps de passage cumulés sur une bande de {BANDE.longueur / 10} × {BANDE.hauteur / 10} cm à imprimer et à
        porter au poignet. L'objectif peut venir d'une de tes {estCoach(moi) ? <Link to="/preferences?onglet=allures">zones d'allure</Link> : "zones d'allure"}, d'un temps
        final ou d'une allure.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        {/* Reglages */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2.5}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">Course</Typography>
              <ToggleButtonGroup exclusive size="small" color="primary" value={codeDistance} onChange={(_e, c) => changerDistance(c)} sx={{ flexWrap: 'wrap' }}>
                {DISTANCES.map((d) => (
                  <ToggleButton key={d.code} value={d.code} sx={{ textTransform: 'none', px: 1.5 }}>{d.libelle}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              {codeDistance === 'AUTRE' && (
                <TextField {...champ} label="Distance (km)" value={distanceAutre} onChange={(e) => { setDistanceAutre(e.target.value); setPasKm(pasParDefaut(nombre(e.target.value))); }} inputProps={{ step: 0.1, min: 1 }} />
              )}
              <TextField size="small" label="Nom imprimé sur le bracelet" value={nom} onChange={(e) => setNom(e.target.value)} inputProps={{ maxLength: 28 }} />
            </Stack>

            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">Objectif</Typography>
              <ToggleButtonGroup exclusive size="small" color="primary" value={mode} onChange={(_e, m) => changerMode(m)} fullWidth>
                <ToggleButton value="zone" sx={{ textTransform: 'none' }}>Zone d'allure</ToggleButton>
                <ToggleButton value="temps" sx={{ textTransform: 'none' }}>Temps</ToggleButton>
                <ToggleButton value="allure" sx={{ textTransform: 'none' }}>Allure</ToggleButton>
              </ToggleButtonGroup>
              {mode === 'zone' && (
                <Stack direction="row" spacing={1}>
                  <TextField select size="small" label="Zone" value={zone ? codeZone : ''} onChange={(e) => setCodeZone(e.target.value)} sx={{ flex: 1 }}>
                    {zones.map((z) => (
                      <MenuItem key={z.code} value={z.code}>{z.nom} · {Math.round(z.pctVMA * 1000) / 10} %</MenuItem>
                    ))}
                  </TextField>
                  <TextField {...champ} label="VMA (km/h)" value={vmaTexte} onChange={(e) => setVmaSaisie(e.target.value)} inputProps={{ step: 0.1, min: 8, max: 30 }} sx={{ width: 120 }} />
                </Stack>
              )}
              {mode === 'temps' && (
                <Stack direction="row" spacing={1}>
                  <TextField {...champ} label="h" value={heures} onChange={(e) => setHeures(e.target.value)} inputProps={{ min: 0, max: 12 }} />
                  <TextField {...champ} label="min" value={minutes} onChange={(e) => setMinutes(e.target.value)} inputProps={{ min: 0, max: 59 }} />
                  <TextField {...champ} label="s" value={secondes} onChange={(e) => setSecondes(e.target.value)} inputProps={{ min: 0, max: 59 }} />
                </Stack>
              )}
              {mode === 'allure' && (
                <Stack direction="row" spacing={1}>
                  <TextField {...champ} label="min / km" value={allureMin} onChange={(e) => setAllureMin(e.target.value)} inputProps={{ min: 2, max: 15 }} />
                  <TextField {...champ} label="s / km" value={allureSec} onChange={(e) => setAllureSec(e.target.value)} inputProps={{ min: 0, max: 59 }} />
                </Stack>
              )}
            </Stack>

            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">Stratégie</Typography>
              <TextField select size="small" value={strategie} onChange={(e) => setStrategie(e.target.value as Strategie)}>
                <MenuItem value="reguliere">Régulière (même allure partout)</MenuItem>
                <MenuItem value="negative">Négative (2ᵉ moitié plus rapide)</MenuItem>
                <MenuItem value="progressive">Progressive (accélère en continu)</MenuItem>
              </TextField>
              {strategie !== 'reguliere' && (
                <Box sx={{ px: 1 }}>
                  <Typography variant="body2">Écart d'allure : {ecartPct.toLocaleString('fr-FR')} %</Typography>
                  <Slider size="small" min={0.5} max={5} step={0.5} value={ecartPct} onChange={(_e, v) => setEcartPct(v as number)} />
                  {modele && (
                    <Typography variant="caption" color="text.secondary">
                      {strategie === 'negative'
                        ? `1re moitié à ${formaterChrono(modele.allureDebut)}/km, 2e moitié à ${formaterChrono(modele.allureFin)}/km`
                        : `Départ à ${formaterChrono(modele.allureDebut)}/km, fin à ${formaterChrono(modele.allureFin)}/km`}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>

            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">Montre GPS</Typography>
              <Box sx={{ px: 1 }}>
                <Typography variant="body2">
                  Correction de distance : {correctionPct > 0 ? `+${correctionPct.toLocaleString('fr-FR')} %` : 'aucune'}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={3}
                  step={0.25}
                  marks={[{ value: 0, label: '0' }, { value: 1, label: '1 %' }, { value: 2, label: '2 %' }, { value: 3, label: '3 %' }]}
                  value={correctionPct}
                  onChange={(_e, v) => setCorrectionPct(v as number)}
                />
                <Typography variant="caption" color="text.secondary" component="div">
                  La montre mesure souvent 0,5 à 1,5 % de plus que la distance officielle (trajectoires, virages,
                  imprécision GPS) et bippe avant les panneaux.
                  {avecGps && valide && (
                    <> Ici : {formaterKm(distanceKm * facteurGps(parametres), 2)} km à la montre, allure à suivre{' '}
                      <b>{formaterChrono(allureMontre(tempsSec / distanceKm, parametres))}/km</b> au lieu de{' '}
                      {formaterChrono(tempsSec / distanceKm)}/km.</>
                  )}
                </Typography>
              </Box>
              {avecGps && (
                <>
                  <Typography variant="body2">Temps de passage aux</Typography>
                  <ToggleButtonGroup exclusive size="small" color="primary" value={reperes} onChange={(_e, v) => v && setReperes(v)}>
                    <ToggleButton value="panneaux" sx={{ textTransform: 'none' }}>Panneaux kilométriques</ToggleButton>
                    <ToggleButton value="montre" sx={{ textTransform: 'none' }}>Bips de la montre</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary">
                    {reperes === 'panneaux'
                      ? 'Temps à lire en passant devant les panneaux de la course.'
                      : 'Temps à comparer au chrono quand la montre bippe chaque kilomètre (lap automatique).'}
                  </Typography>
                </>
              )}
            </Stack>

            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">Bracelet</Typography>
              <TextField select size="small" label="Un passage tous les" value={pasKm} onChange={(e) => setPasKm(Number(e.target.value))}>
                {[1, 2, 2.5, 5].map((p) => <MenuItem key={p} value={p}>{p.toLocaleString('fr-FR')} km</MenuItem>)}
              </TextField>
              <FormControlLabel control={<Checkbox size="small" checked={afficherAllure} onChange={(e) => setAfficherAllure(e.target.checked)} />} label="Allure sous chaque passage" />
              {estMarathon && (
                <FormControlLabel control={<Checkbox size="small" checked={miCourse} onChange={(e) => setMiCourse(e.target.checked)} />} label="Passage au semi (mi-course)" />
              )}
              <ToggleButtonGroup exclusive size="small" value={papier} onChange={(_e, p) => p && setPapier(p)}>
                {(Object.keys(PAPIERS) as Papier[]).map((p) => (
                  <ToggleButton key={p} value={p} sx={{ textTransform: 'none', gap: 1 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: PAPIERS[p].fond, border: 1, borderColor: 'divider' }} />
                    {PAPIERS[p].libelle}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Paper>

        {/* Apercu */}
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          {!valide ? (
            <Alert severity="info">
              {mode === 'zone' && !(vma > 0)
                ? <>Indique ta VMA, ou saisis un record sur la page <Link to="/allures">Allures</Link>.</>
                : 'Indique une distance et un objectif pour générer le bracelet.'}
            </Alert>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={4} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  {[
                    [formaterChrono(tempsSec, avecHeures), 'Temps final'],
                    [`${formaterChrono(tempsSec / distanceKm)}/km`, 'Allure moyenne'],
                    ...(avecGps ? [[`${formaterChrono(allureMontre(tempsSec / distanceKm, parametres))}/km`, 'Allure montre']] : []),
                    [(distanceKm * 3600 / tempsSec).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' km/h', 'Vitesse'],
                    ...(estMarathon ? [[formaterChrono(modele!.tempsA(distanceKm / 2), avecHeures), 'Mi-course']] : []),
                  ].map(([valeur, libelle]) => (
                    <Box key={libelle}>
                      <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{valeur}</Typography>
                      <Typography variant="caption" color="text.secondary">{libelle}</Typography>
                    </Box>
                  ))}
                </Stack>

                <Stack spacing={2}>
                  {bandes.map((b, i) => (
                    <Box key={i}>
                      <Box sx={{ overflowX: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.2))' }}>
                        <Box sx={{ minWidth: 640 }}>
                          <BraceletSvg parametres={parametres} passages={b} index={i} total={bandes.length} />
                        </Box>
                      </Box>
                      {bandes.length > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          Bande {i + 1} sur {bandes.length} : {libellePassage(b[0])} → {libellePassage(b[b.length - 1])}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="contained" startIcon={<PrintIcon />} onClick={imprimer}>Imprimer</Button>
                  <Button startIcon={<ContentCopyIcon />} onClick={copier}>Copier les temps</Button>
                  <Typography variant="body2" color="text.secondary">{message}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Imprime à 100 % (sans « ajuster à la page ») : la règle de 50 mm de la feuille permet de vérifier
                  l'échelle. Protège la bande avec du ruban adhésif large et referme-la sur la zone « COLLER ».
                </Typography>
              </Paper>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
                <Table stickyHeader size="small" aria-label="Temps de passage du bracelet">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Passage</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Temps cumulé</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{avecGps ? 'Allure montre' : 'Allure du tronçon'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Temps du tronçon</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {passages.map((p) => (
                      <TableRow key={p.km} hover sx={p.repere ? { '& td': { fontWeight: 600 } } : undefined}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{libellePassage(p)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formaterChrono(p.temps, avecHeures)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formaterChrono(p.allureTroncon)}/km</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formaterChrono(p.tempsTroncon)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
